// Liens & subrogation — PI Platform outline v2 §1.9.
//
// Sits on the Compliance tab beside the bankruptcy check. Three things
// it has to do well:
//
//   1. Show asserted → final, because the reduction IS the work product
//      and the number the client cares about.
//   2. Keep the negotiation trail visible, since that is what makes the
//      reduction defensible later.
//   3. Never present a projected net as a settled figure. While any
//      lien is open the disbursement is an estimate and says so — a
//      client told "you'll receive $41,200" before the hospital lien is
//      negotiated remembers that number.
//
// Deliberately no red/amber/green status coding. Lien status is not a
// severity scale, and colouring it as one would invent a judgement the
// data does not carry. Open vs resolved reads as text plus a single
// neutral chip.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const LIEN_TYPE_LABELS = {
  medicare: "Medicare", medicaid: "Medicaid", erisa: "ERISA plan",
  private_health: "Health plan", hospital_statutory: "Hospital (statutory)",
  lop: "Letter of protection", attorney_fee: "Prior counsel",
  subrogation: "Subrogation", child_support: "Child support", other: "Other",
};

const STATUS_LABELS = {
  identified: "Identified", verified: "Verified", negotiating: "Negotiating",
  reduced: "Reduced", resolved: "Resolved", waived: "Waived", disputed: "Disputed",
};

const EVENT_LABELS = {
  identified: "Identified", verification_requested: "Verification requested",
  verified: "Verified", reduction_requested: "Reduction requested",
  offer_received: "Offer received", counter_sent: "Counter sent",
  accepted: "Accepted", rejected: "Rejected", resolved: "Resolved",
  waived: "Waived", document_received: "Document received", note: "Note",
};

// Calendar dates, not instants. Slicing the ISO string keeps a
// statutory deadline on the day it actually falls — `new Date(iso)`
// rendered in local time shows UTC midnight as the previous day, and a
// lien perfection deadline off by one is material. Same approach
// MedicalRecordsCard uses for dates of service.
const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);

const money = (v) =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function LiensCard({ matterId, showNotification }) {
  const [liens, setLiens] = useState(null);
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(null);   // lien id with its trail open
  const [detail, setDetail] = useState({});         // id -> full lien
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ lien_type: "hospital_statutory", lienholder_name: "", asserted_amount: "" });

  // Disbursement projection — user supplies the settlement figure; fee
  // terms come from the matter's agreement, not from a box on this card.
  const [gross, setGross] = useState("");
  const [projection, setProjection] = useState(null);
  const [projecting, setProjecting] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/matters/${matterId}/liens`);
      setLiens(d.data || []);
      setSummary(d.summary || null);
    } catch (e) {
      showNotification?.(`Liens failed to load: ${e.message}`, "error");
      setLiens([]);
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);

  const openTrail = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!detail[id]) {
      try {
        const full = await apiFetch(`/api/liens/${id}`);
        setDetail(prev => ({ ...prev, [id]: full }));
      } catch (e) {
        showNotification?.(`Couldn't load that lien: ${e.message}`, "error");
      }
    }
  };

  const addLien = async () => {
    if (!draft.lienholder_name.trim()) {
      showNotification?.("Who is asserting the lien?", "warning");
      return;
    }
    try {
      await apiFetch(`/api/matters/${matterId}/liens`, {
        method: "POST",
        body: JSON.stringify({
          lien_type: draft.lien_type,
          lienholder_name: draft.lienholder_name.trim(),
          asserted_amount: draft.asserted_amount === "" ? null : Number(draft.asserted_amount),
        }),
      });
      setDraft({ lien_type: "hospital_statutory", lienholder_name: "", asserted_amount: "" });
      setAdding(false);
      await load();
    } catch (e) {
      showNotification?.(`Couldn't add the lien: ${e.message}`, "error");
    }
  };

  const patchLien = async (id, changes) => {
    try {
      await apiFetch(`/api/liens/${id}`, { method: "PATCH", body: JSON.stringify(changes) });
      setDetail(prev => { const n = { ...prev }; delete n[id]; return n; });
      await load();
      if (expanded === id) await openTrail(id);
    } catch (e) {
      showNotification?.(`Update failed: ${e.message}`, "error");
    }
  };

  const project = async () => {
    if (!gross) {
      showNotification?.("Enter the gross settlement first.", "warning");
      return;
    }
    setProjecting(true);
    try {
      const p = new URLSearchParams({ gross_settlement: String(Number(gross)) });
      setProjection(await apiFetch(`/api/matters/${matterId}/disbursement?${p}`));
    } catch (e) {
      showNotification?.(`Projection failed: ${e.message}`, "error");
    }
    setProjecting(false);
  };

  if (liens === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } }, "Loading liens…"),
      ));
  }

  return React.createElement("div", { style: cardStyle },
    // ── Header ──────────────────────────────────────────────
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "14px" },
    },
      React.createElement("div", null,
        React.createElement("h2", { style: h2Style }, "Liens & Subrogation"),
        React.createElement("div", { style: { fontSize: "13px", color: C.textMuted, marginTop: "4px" } },
          liens.length === 0
            ? "Nothing asserted against this settlement yet."
            : `${liens.length} lien${liens.length === 1 ? "" : "s"}` +
              (summary?.open_count ? ` · ${summary.open_count} still open` : " · all resolved"),
        ),
      ),
      React.createElement("button", {
        onClick: () => setAdding(a => !a),
        style: {
          padding: "7px 14px", borderRadius: "8px", border: `1px solid ${C.navy}`,
          background: "transparent", color: C.navy, fontSize: "12px",
          fontWeight: 600, cursor: "pointer", flexShrink: 0,
        },
      }, adding ? "Cancel" : "Add lien"),
    ),

    // ── Totals ──────────────────────────────────────────────
    // The reduction is the headline: it is what the firm achieved and
    // what the client keeps.
    summary && liens.length > 0 && React.createElement("div", {
      style: {
        display: "flex", gap: "22px", flexWrap: "wrap", padding: "12px 14px",
        background: C.warmWhite, borderRadius: "8px", marginBottom: "14px",
      },
    },
      _stat("Asserted", money(summary.total_asserted)),
      _stat("Resolved for", money(summary.total_final)),
      _stat("Reduction achieved", money(summary.total_reduction)),
    ),

    // ── Add form ────────────────────────────────────────────
    adding && React.createElement("div", {
      style: {
        display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end",
        padding: "12px 14px", background: C.warmWhite, borderRadius: "8px", marginBottom: "14px",
      },
    },
      _field("Type",
        React.createElement("select", {
          value: draft.lien_type,
          onChange: e => setDraft({ ...draft, lien_type: e.target.value }),
          style: inputStyle,
        }, Object.entries(LIEN_TYPE_LABELS).map(([v, label]) =>
          React.createElement("option", { key: v, value: v }, label)))),
      _field("Lienholder",
        React.createElement("input", {
          type: "text", value: draft.lienholder_name,
          onChange: e => setDraft({ ...draft, lienholder_name: e.target.value }),
          placeholder: "e.g. Springhill Memorial",
          style: { ...inputStyle, minWidth: "220px" },
        })),
      _field("Amount asserted",
        React.createElement("input", {
          type: "number", value: draft.asserted_amount,
          onChange: e => setDraft({ ...draft, asserted_amount: e.target.value }),
          placeholder: "optional",
          style: { ...inputStyle, width: "140px" },
        })),
      React.createElement("button", {
        onClick: addLien,
        style: {
          padding: "8px 16px", borderRadius: "8px", border: "none",
          background: C.gold, color: "#fff", fontSize: "12px", fontWeight: 700, cursor: "pointer",
        },
      }, "Add"),
    ),

    // ── The list ────────────────────────────────────────────
    liens.length > 0 && React.createElement("div", {
      style: { border: `1px solid ${C.warmGray}60`, borderRadius: "8px", overflow: "hidden" },
    },
      ...liens.map((l, i) => React.createElement(React.Fragment, { key: l.id },
        React.createElement("div", {
          style: {
            padding: "11px 13px",
            borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}40`,
            background: l.is_open ? "#fff" : C.warmWhite,
          },
        },
          React.createElement("div", {
            style: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
          },
            React.createElement("div", { style: { flex: "1 1 220px", minWidth: 0 } },
              React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
                l.lienholder_name),
              React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
                LIEN_TYPE_LABELS[l.lien_type] || l.lien_type,
                " · ", STATUS_LABELS[l.status] || l.status,
                l.claim_number ? ` · #${l.claim_number}` : "",
                l.statutory_deadline
                  ? ` · perfect by ${dateOnly(l.statutory_deadline)}`
                  : "",
              ),
              // A lienholder who missed their own perfection deadline may
              // hold an unenforceable lien — money back to the client.
              // Stated as fact rather than colour-coded: this is a
              // prompt to check, not a severity rating.
              _deadlinePassed(l) && React.createElement("div", {
                style: { fontSize: "11px", color: C.navy, marginTop: "3px", fontWeight: 600 },
              }, "Perfection deadline has passed and the lien is not marked perfected — "
                 + "check whether it is enforceable before paying it."),
            ),

            // asserted → final, the whole point
            React.createElement("div", { style: { fontSize: "12px", color: C.textDark, whiteSpace: "nowrap" } },
              money(l.asserted_amount),
              l.final_amount !== null && React.createElement("span", null,
                React.createElement("span", { style: { color: C.textLight } }, "  →  "),
                React.createElement("span", { style: { fontWeight: 700 } }, money(l.final_amount)),
              ),
            ),
            l.reduction_amount !== null && React.createElement("span", {
              style: {
                fontSize: "11px", fontWeight: 700, color: C.navy,
                background: `${C.gold}20`, padding: "3px 9px", borderRadius: "9999px", whiteSpace: "nowrap",
              },
            }, `saved ${money(l.reduction_amount)}`),

            React.createElement("button", {
              onClick: () => openTrail(l.id),
              style: {
                border: "none", background: "none", padding: "4px 8px", cursor: "pointer",
                fontSize: "12px", fontWeight: 600, color: C.navy,
              },
            }, expanded === l.id ? "Hide" : "History"),
          ),

          // ── Negotiation trail ─────────────────────────────
          expanded === l.id && React.createElement("div", {
            style: { marginTop: "10px", paddingTop: "10px", borderTop: `1px solid ${C.warmGray}40` },
          },
            !detail[l.id]
              ? React.createElement("div", { style: { fontSize: "12px", color: C.textMuted } }, "Loading…")
              : React.createElement("div", null,
                  React.createElement("div", {
                    style: { display: "flex", flexDirection: "column", gap: "5px", marginBottom: "10px" },
                  },
                    ...(detail[l.id].events || []).map(ev => React.createElement("div", {
                      key: ev.id,
                      style: { display: "flex", gap: "10px", fontSize: "12px", color: C.textDark },
                    },
                      React.createElement("span", { style: { color: C.textLight, minWidth: "88px" } },
                        ev.occurred_at ? new Date(ev.occurred_at).toLocaleDateString() : ""),
                      React.createElement("span", { style: { fontWeight: 600, minWidth: "150px" } },
                        EVENT_LABELS[ev.event_type] || ev.event_type),
                      React.createElement("span", null, ev.amount !== null ? money(ev.amount) : ""),
                      ev.notes && React.createElement("span", { style: { color: C.textMuted } }, ev.notes),
                    )),
                  ),
                  // Quick actions — the three beats that make up most
                  // lien work.
                  React.createElement("div", { style: { display: "flex", gap: "8px", flexWrap: "wrap" } },
                    _quiet("Record reduction request", () =>
                      apiFetch(`/api/liens/${l.id}/events`, {
                        method: "POST", body: JSON.stringify({ event_type: "reduction_requested" }),
                      }).then(() => { setDetail(p => { const n = { ...p }; delete n[l.id]; return n; }); openTrail(l.id); })),
                    _quiet("Mark verified", () => {
                      const v = window.prompt("Amount the lienholder confirmed in writing:");
                      if (v) patchLien(l.id, { verified_amount: Number(v), status: "verified" });
                    }),
                    _quiet("Resolve at…", () => {
                      const v = window.prompt("Final agreed amount:");
                      if (v) patchLien(l.id, { final_amount: Number(v), status: "resolved" });
                    }),
                  ),
                ),
          ),
        ),
      )),
    ),

    // ── Disbursement projection ─────────────────────────────
    React.createElement("div", {
      style: { marginTop: "16px", paddingTop: "14px", borderTop: `1px solid ${C.warmGray}60` },
    },
      React.createElement("h3", {
        style: { fontSize: "12px", color: C.textMuted, textTransform: "uppercase",
                 letterSpacing: "0.06em", fontWeight: 600, margin: "0 0 8px" },
      }, "What the client nets"),
      React.createElement("div", { style: { display: "flex", gap: "8px", alignItems: "flex-end", flexWrap: "wrap" } },
        _field("Gross settlement",
          React.createElement("input", {
            type: "number", value: gross, onChange: e => setGross(e.target.value),
            placeholder: "0.00", style: { ...inputStyle, width: "150px" },
          })),
        // No costs input any more. They come from what was recorded on
        // the Case Costs card below — a typed figure here could silently
        // disagree with the record, and the disagreement would be
        // invisible in the resulting statement.
        React.createElement("button", {
          onClick: project, disabled: projecting,
          style: {
            padding: "8px 16px", borderRadius: "8px", border: `1px solid ${C.navy}`,
            background: "transparent", color: C.navy, fontSize: "12px",
            fontWeight: 600, cursor: projecting ? "wait" : "pointer",
          },
        }, projecting ? "Calculating…" : "Calculate"),
      ),

      projection && React.createElement("div", { style: { marginTop: "12px" } },
        // The estimate/figure distinction, stated before the numbers
        // rather than under them.
        React.createElement("div", {
          style: {
            fontSize: "12px", fontWeight: 700, marginBottom: "8px",
            color: projection.is_final ? C.navy : C.textMuted,
          },
        }, projection.is_final
            ? "Final — every lien resolved and fee terms on file"
            : "Estimate only — not a disbursement figure"),

        React.createElement("div", { style: { border: `1px solid ${C.warmGray}60`, borderRadius: "8px", overflow: "hidden" } },
          _row("Gross settlement", money(projection.gross_settlement)),
          _row(`Attorney fee${projection.attorney_fee_rate
                ? ` (${(projection.attorney_fee_rate * 100).toFixed(2).replace(/\\.?0+$/, "")}% of ${
                    projection.fee_basis === "gross" ? "gross" : "net of costs"})`
                : ""}`,
               projection.attorney_fee === null ? "not calculated" : `− ${money(projection.attorney_fee)}`),
          _row(projection.case_costs_source === "tracked"
                 ? "Case costs (recorded below)" : "Case costs",
               `− ${money(projection.case_costs)}`),
          _row("Liens", `− ${money(projection.lien_total)}`),
          _row("Client receives",
               projection.net_to_client === null ? "—" : money(projection.net_to_client), true),
        ),

        (projection.warnings || []).length > 0 && React.createElement("ul", {
          style: { margin: "10px 0 0", paddingLeft: "18px", fontSize: "12px", color: C.textMuted, lineHeight: 1.6 },
        }, ...projection.warnings.map((w, i) =>
          React.createElement("li", { key: i }, w))),
      ),
    ),
  );
}

// ── small helpers ──────────────────────────────────────────
const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const inputStyle = {
  padding: "7px 10px", borderRadius: "6px", border: "1px solid #E8E3DC",
  fontSize: "13px", background: "#fff",
};

function _field(label, control) {
  return React.createElement("label", { style: { display: "flex", flexDirection: "column", gap: "4px" } },
    React.createElement("span", {
      style: { fontSize: "10px", color: "#9A948C", fontWeight: 700,
               textTransform: "uppercase", letterSpacing: "0.06em" },
    }, label),
    control);
}

function _stat(label, value) {
  return React.createElement("div", null,
    React.createElement("div", {
      style: { fontSize: "10px", color: "#9A948C", fontWeight: 700,
               textTransform: "uppercase", letterSpacing: "0.06em" },
    }, label),
    React.createElement("div", { style: { fontSize: "16px", fontWeight: 700, color: "#1B2A4A", marginTop: "2px" } },
      value));
}

function _row(label, value, emphasise) {
  return React.createElement("div", {
    key: label,
    style: {
      display: "flex", justifyContent: "space-between", padding: "9px 13px",
      borderTop: "1px solid #E8E3DC40",
      background: emphasise ? "#FAF8F5" : "#fff",
      fontSize: emphasise ? "14px" : "13px",
      fontWeight: emphasise ? 700 : 400,
      color: "#1B2A4A",
    },
  },
    React.createElement("span", null, label),
    React.createElement("span", null, value));
}

// True only when we know the deadline passed AND perfection was not
// recorded. `is_perfected === null` means nobody has checked yet, which
// is exactly the case worth surfacing; `true` means it was perfected
// and there is nothing to raise.
function _deadlinePassed(l) {
  const d = dateOnly(l.statutory_deadline);
  if (!d || l.is_perfected === true) return false;
  return d < new Date().toISOString().slice(0, 10);
}

function _quiet(label, onClick) {
  return React.createElement("button", {
    key: label, onClick,
    style: {
      border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
      padding: "5px 11px", fontSize: "11px", fontWeight: 600,
      color: "#1B2A4A", cursor: "pointer",
    },
  }, label);
}
